"use client"

/**
 * Program standards on a question, kept out of the way.
 *
 * Most questions need only a course content category. A program standard (NCLEX
 * client needs, accreditor standards, outcomes) is often already covered by the
 * director's mapping, so the Properties panel shows one quiet row that says
 * where the question counts today and opens the category navigation, where
 * each standard is browsed and tagged. Nothing here asks for attention unless a
 * required standard is missing or Leo has something to suggest.
 *
 * Who may tag which standard is the standard's own setting. A tag a faculty
 * member places waits for the director to verify it.
 */

import * as React from "react"

import { ChosenCategory } from "@/components/question-composer/chosen-categories"
import { useQuestionComposerContext } from "@/components/question-composer/composer-context"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { LeoSpottedCard } from "@/lib/question-quality/leo-spotted-queue"
import { Tip } from "@/components/ui/tip"
import { useCategoryCrosswalk } from "@/hooks/use-category-crosswalk"
import { useTaggableStandards, useViewOnlyStandards } from "@/hooks/use-category-dimensions"
import { useExamCategories } from "@/hooks/use-exam-categories"
import {
  buildDimensionLookup,
  confidenceLabel,
  standardsForQuestion,
  type CategoryDimension,
  type CategoryProvenanceMap,
  type ExamCategory,
} from "@/lib/exam-categories"

/** Cross-tree suggestions are held to a higher bar than content ones. */
export const STANDARD_SUGGESTION_MIN_CONFIDENCE = 0.7

/** Opens the standards list in the category navigation, instead of one tree. */
export const STANDARDS_HUB_ID = "__standards"

function useStandardsReading(value: string[], courseTrees: CategoryDimension[]) {
  const standards = useTaggableStandards()
  const { categories } = useExamCategories()
  const { links } = useCategoryCrosswalk()
  const { categorySuggestions } = useQuestionComposerContext()
  const lookup = React.useMemo(() => buildDimensionLookup(categories), [categories])
  const contentIds = React.useMemo(
    () =>
      value.filter(
        (id) =>
          ![...standards, ...courseTrees].some((dimension) => dimension.id === lookup.get(id)),
      ),
    [courseTrees, lookup, standards, value],
  )
  const fromMapping = React.useMemo(
    () => standardsForQuestion(contentIds, links, categories),
    [categories, contentIds, links],
  )
  const tagged = value.filter((id) => standards.some((s) => s.id === lookup.get(id)))
  const suggestions = categorySuggestions.filter(
    (row) =>
      standards.some((s) => s.id === row.dimensionId) &&
      row.confidence >= STANDARD_SUGGESTION_MIN_CONFIDENCE &&
      !value.includes(row.categoryId),
  )
  const missingRequired = standards.filter(
    (dimension) =>
      dimension.required &&
      !tagged.some((id) => lookup.get(id) === dimension.id) &&
      !fromMapping.some((id) => lookup.get(id) === dimension.id),
  )
  return { standards, categories, lookup, fromMapping, tagged, suggestions, missingRequired }
}

/**
 * Leo's word on a required standard with nothing tagged, as cards for the one
 * classification card Leo shows (at the top of Properties and of the category
 * navigation). Standard suggestions come from the same card's category reading.
 */
export function useRequiredStandardCards(value: string[], courseTrees: CategoryDimension[] = []): LeoSpottedCard[] {
  const { missingRequired } = useStandardsReading(value, courseTrees)
  if (missingRequired.length === 0) return []
  return [
    {
      id: "standards:required",
      kind: "category",
      dimension: "category",
      badge: "Standard",
      header: `${missingRequired.map((d) => d.name).join(", ")} ${missingRequired.length === 1 ? "needs" : "need"} a tag`,
      body: "A required standard has nothing tagged on this question yet.",
      primaryLabel: "Choose",
      issues: [],
      state: "open",
    },
  ]
}

/**
 * Under Category: the standards this question counts toward, as chips, and a
 * way into the standards. Leo's suggestions live in the classification card at
 * the top, not here.
 */
export function StandardsSummaryRow({
  value,
  onOpen,
  courseTrees = [],
  disabled = false,
}: {
  value: string[]
  onOpen: () => void
  courseTrees?: CategoryDimension[]
  disabled?: boolean
}) {
  const { standards, categories, lookup, fromMapping, tagged } = useStandardsReading(value, courseTrees)
  if (standards.length === 0 && courseTrees.length === 0) return null

  const nameOf = (id: string) => categories.find((row) => row.id === id)?.name
  const shownIds = [...new Set([...tagged, ...fromMapping])]
  const names = shownIds.map(nameOf).filter((name): name is string => Boolean(name))
  const courseTreeNames = value
    .filter((id) => courseTrees.some((t) => t.id === lookup.get(id)))
    .map(nameOf)
    .filter((name): name is string => Boolean(name))
  const counted = [...names, ...courseTreeNames]
  const MAX_CHIPS = 3

  if (counted.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Counts toward</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onOpen}
          className="ms-auto h-6 px-2 text-xs"
        >
          View standards
        </Button>
      </div>
      <ul aria-label="Standards this question counts toward" className="m-0 flex list-none flex-wrap gap-1.5 p-0">
        {counted.slice(0, MAX_CHIPS).map((name) => (
          <li key={name}>
            <Badge variant="outline" className="max-w-full font-normal">
              <span className="truncate">{name}</span>
            </Badge>
          </li>
        ))}
        {counted.length > MAX_CHIPS ? (
          <li>
            <Badge variant="secondary" className="font-normal tabular-nums">
              +{counted.length - MAX_CHIPS} more
            </Badge>
          </li>
        ) : null}
      </ul>
    </div>
  )
}

/** The standards as collapsible sections inside the category navigation, one open at a time. */
export function StandardsAccordion({
  value,
  provenance,
  onChange,
  openId,
  onOpenChange,
  renderTree,
  onVerify,
  canVerify,
  courseTrees = [],
  courseCode,
}: {
  value: string[]
  provenance: CategoryProvenanceMap
  onChange: (ids: string[], meta?: { aiIds?: string[] }) => void
  openId: string | null
  onOpenChange: (dimensionId: string | null) => void
  renderTree: (dimension: CategoryDimension, readOnly: boolean) => React.ReactNode
  onVerify?: (categoryId: string) => void
  canVerify?: boolean
  courseTrees?: CategoryDimension[]
  courseCode?: string
}) {
  const { standards, categories, lookup, fromMapping, suggestions } = useStandardsReading(
    value,
    courseTrees,
  )
  const { dismissCategorySuggestion } = useQuestionComposerContext()

  // Show what this question is actually about: a required standard, one it is
  // already tagged with or mapped to, or one Leo has a suggestion for. The rest
  // are not offered: the director's mapping counts the question toward them.
  const isRelevant = (dimension: CategoryDimension) =>
    dimension.required ||
    openId === dimension.id ||
    [...value, ...fromMapping].some((id) => lookup.get(id) === dimension.id) ||
    suggestions.some((row) => row.dimensionId === dimension.id)
  const relevant = standards.filter(isRelevant)
  // Standards this person cannot tag with, shown view-only where the question
  // already counts toward them, so the tree is never silently missing.
  const viewOnly = useViewOnlyStandards().filter(
    (dimension) =>
      openId === dimension.id ||
      [...value, ...fromMapping].some((id) => lookup.get(id) === dimension.id),
  )

  const block = (dimension: CategoryDimension, rootLabel: string, locked = false) => {
    const chosen = value
      .filter((id) => lookup.get(id) === dimension.id)
      .map((id) => categories.find((row) => row.id === id))
      .filter((row): row is ExamCategory => Boolean(row))
    const open = openId === dimension.id
    return (
      <li key={dimension.id} className="flex shrink-0 flex-col gap-2 rounded-lg border border-border bg-card p-3">
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(open ? null : dimension.id)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="min-w-0 flex-1 break-words text-sm font-medium text-foreground">
              {dimension.name}
              {dimension.required ? <span className="text-destructive"> *</span> : null}
              {locked ? (
                <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                  <i className="fa-light fa-lock me-1" aria-hidden="true" />
                  View only. Set by {dimension.lockedFor?.unlockedBy ?? "the program admin"}.
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {chosen.length > 0 ? `${chosen.length} tagged` : "None"}
            </span>
            <i
              className={`fa-light ${open ? "fa-chevron-up" : "fa-chevron-down"} ms-auto shrink-0 text-xs text-muted-foreground`}
              aria-hidden="true"
            />
          </button>
        </div>
        {open ? <div className="flex max-h-96 min-h-0 flex-col">{renderTree(dimension, locked)}</div> : null}
        {!open && chosen.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {chosen.map((category) => (
              <ChosenCategory
                key={category.id}
                category={category}
                categories={categories}
                rootLabel={rootLabel}
                record={provenance[category.id]}
                disabled={false}
                onOpen={() => onOpenChange(dimension.id)}
                onRemove={locked ? undefined : () => onChange(value.filter((id) => id !== category.id))}
                onVerify={canVerify ? () => onVerify?.(category.id) : undefined}
              />
            ))}
          </ul>
        ) : null}
      </li>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {relevant.map((dimension) => block(dimension, dimension.name))}
        {viewOnly.map((dimension) => block(dimension, dimension.name, true))}
      </ul>
      {courseTrees.length > 0 ? (
        <section className="flex flex-col gap-2">
          <p className="m-0 text-xs font-semibold text-foreground">{courseCode ?? "This course"}</p>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {courseTrees.map((dimension) => block(dimension, dimension.name))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
